import * as React from 'react';
import { Button, Modal, ModalBody, ModalHeader, ModalVariant } from '@patternfly/react-core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { URL_ERROR_MSG, URL_REGEX } from '../../../../../utils/validation-utils';
import { ComponentProps } from '../../../../modal/createModalLauncher';
import BugFormContent from './BugFormContent';
import CVEFormContent from './CVEFormContent';
import { dateFormat } from './UploadDate';

export enum IssueType {
  BUG = 'bug',
  CVE = 'cve',
}

type AddIssueModalProps = ComponentProps & {
  bugArrayHelper: (values) => void;
  issueType: IssueType;
};

const IssueFormSchema = yup.object({
  id: yup.string().required('Required'),
  source: yup.string().matches(URL_REGEX, URL_ERROR_MSG).required('Required'),
});

const CVEFormSchema = yup.object({
  key: yup.string().required('Required'),
});

export const AddIssueModal: React.FC<React.PropsWithChildren<AddIssueModalProps>> = ({
  onClose,
  bugArrayHelper,
  issueType,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const isBug = issueType === IssueType.BUG;

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const setValues = React.useCallback(
    (fields) => {
      bugArrayHelper(fields);
      onClose();
    },
    [onClose, bugArrayHelper],
  );

  return (
    <>
      <Button variant="primary" onClick={handleModalToggle} data-test="modal-launch-btn">
        {isBug ? 'Add Jira issue' : 'Add CVE'}
      </Button>
      <Modal
        variant={ModalVariant.medium}
        isOpen={isModalOpen}
        onClose={handleModalToggle}
        data-test="add-issue-modal"
      >
        <ModalHeader title={isBug ? 'Add Jira issue' : 'Add a CVE'} />
        <ModalBody>
          <Formik
            onSubmit={setValues}
            initialValues={
              isBug
                ? { id: '', source: '', uploadDate: dateFormat(new Date()) }
                : {
                    key: '',
                    component: '',
                    packages: [],
                  }
            }
            validationSchema={isBug ? IssueFormSchema : CVEFormSchema}
          >
            {isBug ? (
              <BugFormContent modalToggle={handleModalToggle} />
            ) : (
              <CVEFormContent modalToggle={handleModalToggle} />
            )}
          </Formik>
        </ModalBody>
      </Modal>
    </>
  );
};
